import { createStore } from "zustand/vanilla";
import type { StoreApi } from "zustand/vanilla";

export interface AdminListContext {
  readonly path: string;
  readonly page: number;
  readonly filters: Readonly<Record<string, string>>;
  readonly entryKey?: string;
  readonly scrollY?: number;
  readonly tableScrollLeft?: number;
  readonly triggerKey?: string;
}

export interface UnresolvedAdminCommand {
  readonly operation: string;
  readonly targetId: string;
  readonly startedAt: string;
}

export interface AdminUiState {
  readonly sessionEpoch: number;
  readonly sidebarCollapsed: boolean;
  readonly listContexts: Readonly<Record<string, AdminListContext>>;
  readonly menuPaths: Readonly<Record<string, string>>;
  readonly navigationEntries: readonly string[];
  readonly navigationIndex: number;
  readonly flashNotices: Readonly<Record<string, string>>;
  readonly unresolvedCommands: Readonly<Record<string, UnresolvedAdminCommand>>;
  setSidebarCollapsed(collapsed: boolean): void;
  rememberListContext(key: string, context: AdminListContext, preserveViewport?: boolean): void;
  rememberMenuPath(screen: string, path: string): void;
  forgetListContext(key: string): void;
  recordNavigation(entryKey: string, navigationType: "POP" | "PUSH" | "REPLACE"): void;
  setFlashNotice(key: string, notice: string): void;
  consumeFlashNotice(key: string): void;
  markCommandUnresolved(key: string, command: UnresolvedAdminCommand): void;
  resolveCommand(key: string): void;
  resetForSession(sessionEpoch: number): void;
}

export type AdminUiStore = StoreApi<AdminUiState>;

export function createAdminUiStore(sessionEpoch = 0): AdminUiStore {
  return createStore<AdminUiState>()((set) => ({
    sessionEpoch,
    sidebarCollapsed: false,
    listContexts: {},
    menuPaths: {},
    navigationEntries: [],
    navigationIndex: -1,
    flashNotices: {},
    unresolvedCommands: {},
    setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
    rememberListContext: (key, context, preserveViewport = false) => set((state) => {
      const previous = state.listContexts[key];
      const next = preserveViewport && previous ? {
        ...context,
        entryKey: previous.entryKey,
        scrollY: previous.scrollY,
        tableScrollLeft: previous.tableScrollLeft,
        triggerKey: previous.triggerKey,
      } : context;
      return { listContexts: { ...state.listContexts, [key]: next } };
    }),
    rememberMenuPath: (screen, path) => set((state) => ({
      menuPaths: { ...state.menuPaths, [screen]: path },
    })),
    forgetListContext: (key) => set((state) => {
      const listContexts = { ...state.listContexts };
      delete listContexts[key];
      return { listContexts };
    }),
    recordNavigation: (entryKey, navigationType) => set((state) => {
      if (!entryKey) return state;
      if (state.navigationEntries.length === 0) {
        return { navigationEntries: [entryKey], navigationIndex: 0 };
      }
      if (navigationType === "PUSH") {
        const entries = state.navigationEntries.slice(0, state.navigationIndex + 1);
        if (entries.at(-1) === entryKey) return state;
        return { navigationEntries: [...entries, entryKey], navigationIndex: entries.length };
      }
      if (navigationType === "REPLACE") {
        const entries = [...state.navigationEntries];
        entries[Math.max(state.navigationIndex, 0)] = entryKey;
        return { navigationEntries: entries, navigationIndex: Math.max(state.navigationIndex, 0) };
      }
      const existingIndex = state.navigationEntries.indexOf(entryKey);
      if (existingIndex >= 0) return { navigationIndex: existingIndex };
      return { navigationEntries: [entryKey], navigationIndex: 0 };
    }),
    setFlashNotice: (key, notice) => set((state) => ({
      flashNotices: { ...state.flashNotices, [key]: notice },
    })),
    consumeFlashNotice: (key) => set((state) => {
      const flashNotices = { ...state.flashNotices };
      delete flashNotices[key];
      return { flashNotices };
    }),
    markCommandUnresolved: (key, command) => set((state) => ({
      unresolvedCommands: { ...state.unresolvedCommands, [key]: command },
    })),
    resolveCommand: (key) => set((state) => {
      const unresolvedCommands = { ...state.unresolvedCommands };
      delete unresolvedCommands[key];
      return { unresolvedCommands };
    }),
    resetForSession: (nextSessionEpoch) => set({
      sessionEpoch: nextSessionEpoch,
      sidebarCollapsed: false,
      listContexts: {},
      menuPaths: {},
      navigationEntries: [],
      navigationIndex: -1,
      flashNotices: {},
      unresolvedCommands: {},
    }),
  }));
}
